import { useCallback, useState, type DragEvent } from 'react';
import type { StoryboardContextValue } from '../../context';
import type { StoryboardSegment } from '../../types';

export interface DragStateSnapshot {
  draggingSegmentId: string | null;
  dropTargetSegmentId: string | null;
  dropPosition: 'before' | 'after' | null;
  draggingOverAddButton: boolean;
}

type UseDragManagerParams = Pick<
  StoryboardContextValue,
  'segments' | 'mutating' | 'reorderSegments'
>;

interface UseDragManagerResult {
  state: DragStateSnapshot;
  handleDragStart: (event: DragEvent<HTMLElement>, segmentId: string) => void;
  handleDragEnd: () => void;
  handleDragOverSegment: (event: DragEvent<HTMLElement>, targetSegmentId: string) => void;
  handleDragLeaveSegment: (event: DragEvent<HTMLElement>, targetSegmentId: string) => void;
  handleDropOnSegment: (event: DragEvent<HTMLElement>, targetSegmentId: string) => Promise<void>;
  handleDragOverAddButton: (event: DragEvent<HTMLElement>) => void;
  handleDragLeaveAddButton: (event: DragEvent<HTMLElement>) => void;
  handleDropOnAddButton: (event: DragEvent<HTMLElement>) => Promise<void>;
}

const calculateDropPosition = (
  event: DragEvent<HTMLElement>,
  target: HTMLElement
): 'before' | 'after' => {
  const bounding = target.getBoundingClientRect();
  const midpoint = bounding.left + bounding.width / 2;
  return event.clientX < midpoint ? 'before' : 'after';
};

const insertAfterReorder = (
  segments: StoryboardSegment[],
  sourceId: string,
  targetId: string,
  position: 'before' | 'after'
): number | null => {
  const fromIndex = segments.findIndex((segment) => segment.segmentId === sourceId);
  const toIndex = segments.findIndex((segment) => segment.segmentId === targetId);
  if (fromIndex < 0 || toIndex < 0) {
    return null;
  }
  const working = [...segments];
  const [moved] = working.splice(fromIndex, 1);
  const targetIndexAfterRemoval = working.findIndex((segment) => segment.segmentId === targetId);
  const insertionIndex =
    position === 'before' ? targetIndexAfterRemoval : targetIndexAfterRemoval + 1;
  working.splice(Math.max(0, Math.min(insertionIndex, working.length)), 0, moved);
  return working.findIndex((segment) => segment.segmentId === moved.segmentId);
};

export const useDragManager = (params: UseDragManagerParams): UseDragManagerResult => {
  const { segments, mutating, reorderSegments } = params;
  const [draggingSegmentId, setDraggingSegmentId] = useState<string | null>(null);
  const [dropTargetSegmentId, setDropTargetSegmentId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | null>(null);
  const [draggingOverAddButton, setDraggingOverAddButton] = useState<boolean>(false);

  const reset = useCallback(() => {
    setDraggingSegmentId(null);
    setDropTargetSegmentId(null);
    setDropPosition(null);
    setDraggingOverAddButton(false);
  }, []);

  const handleDragStart = useCallback(
    (event: DragEvent<HTMLElement>, segmentId: string) => {
      if (mutating) {
        event.preventDefault();
        return;
      }
      setDraggingSegmentId(segmentId);
      setDropTargetSegmentId(null);
      setDropPosition(null);
      event.dataTransfer.setData('text/plain', segmentId);
      event.dataTransfer.effectAllowed = 'move';
    },
    [mutating]
  );

  const handleDragEnd = useCallback(() => {
    reset();
  }, [reset]);

  const handleDragOverSegment = useCallback(
    (event: DragEvent<HTMLElement>, targetSegmentId: string) => {
      if (!draggingSegmentId || draggingSegmentId === targetSegmentId) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDropTargetSegmentId(targetSegmentId);
      setDropPosition(calculateDropPosition(event, event.currentTarget as HTMLElement));
      setDraggingOverAddButton(false);
    },
    [draggingSegmentId]
  );

  const handleDragLeaveSegment = useCallback(
    (event: DragEvent<HTMLElement>, targetSegmentId: string) => {
      if (!draggingSegmentId || draggingSegmentId === targetSegmentId) {
        return;
      }
      const related = event.relatedTarget as HTMLElement | null;
      if (related && event.currentTarget.contains(related)) {
        return;
      }
      setDropTargetSegmentId((current) => (current === targetSegmentId ? null : current));
      setDropPosition((current) =>
        current && dropTargetSegmentId === targetSegmentId ? null : current
      );
    },
    [draggingSegmentId, dropTargetSegmentId]
  );

  const handleDropOnSegment = useCallback(
    async (event: DragEvent<HTMLElement>, targetSegmentId: string) => {
      if (!draggingSegmentId) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const sourceId = draggingSegmentId ?? event.dataTransfer.getData('text/plain');
      if (!sourceId || sourceId === targetSegmentId || !dropPosition) {
        reset();
        return;
      }
      const finalIndex = insertAfterReorder(segments, sourceId, targetSegmentId, dropPosition);
      if (finalIndex == null) {
        reset();
        return;
      }
      try {
        await reorderSegments(sourceId, finalIndex);
      } finally {
        reset();
      }
    },
    [draggingSegmentId, dropPosition, reorderSegments, reset, segments]
  );

  const handleDragOverAddButton = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!draggingSegmentId) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'move';
      setDropTargetSegmentId(null);
      setDropPosition(null);
      setDraggingOverAddButton(true);
    },
    [draggingSegmentId]
  );

  const handleDragLeaveAddButton = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!draggingSegmentId) {
        return;
      }
      const related = event.relatedTarget as HTMLElement | null;
      if (related && event.currentTarget.contains(related)) {
        return;
      }
      setDraggingOverAddButton(false);
    },
    [draggingSegmentId]
  );

  const handleDropOnAddButton = useCallback(
    async (event: DragEvent<HTMLElement>) => {
      if (!draggingSegmentId) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const sourceId = draggingSegmentId ?? event.dataTransfer.getData('text/plain');
      if (!sourceId) {
        reset();
        return;
      }
      const fromIndex = segments.findIndex((segment) => segment.segmentId === sourceId);
      const finalIndex = Math.max(segments.length - 1, 0);
      if (fromIndex < 0 || fromIndex === finalIndex) {
        reset();
        return;
      }
      try {
        await reorderSegments(sourceId, finalIndex);
      } finally {
        reset();
      }
    },
    [draggingSegmentId, reorderSegments, reset, segments]
  );

  return {
    state: {
      draggingSegmentId,
      dropTargetSegmentId,
      dropPosition,
      draggingOverAddButton,
    },
    handleDragStart,
    handleDragEnd,
    handleDragOverSegment,
    handleDragLeaveSegment,
    handleDropOnSegment,
    handleDragOverAddButton,
    handleDragLeaveAddButton,
    handleDropOnAddButton,
  };
};
